import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import useAddMembersToPermissionGroup from 'modules/permissions/hooks/useAddMembersToPermissionGroup'
import permissionGroups from 'modules/permissions/store/collections/permissionGroups'
import type PermissionGroup from 'modules/permissions/store/models/PermissionGroup'

interface Props {
  onClose: () => void
  permissionGroup: PermissionGroup
  memberCount: number
  memberIds: string[]
}

const DeleteModal = ({ onClose, permissionGroup, memberCount, memberIds }: Props) => {
  const [addMembers] = useAddMembersToPermissionGroup()
  const [destinationSelected, setDestinationSelected] = useState(memberCount === 0)

  const defaultDestination = permissionGroups.basicGroup
  if (!defaultDestination) throw new Error('Basic group not found')

  const form = useLazyRef(
    () =>
      new Form(
        {
          permissions_group_id: defaultDestination.id,
        },
        {
          permissions_group_id: 'string',
        }
      )
  ).current

  const description = destinationSelected
    ? i18n.t('settings.permissions.delete.confirm_description')
    : i18n.t('settings.permissions.delete.description')

  const selectOptions = () => {
    return permissionGroups.models
      .filter((pg) => pg.attributes.id !== permissionGroup.attributes.id)
      .map((pg) => ({
        value: pg.attributes.id,
        label: pg.attributes.name,
      }))
  }

  const onDeleteConfirm = async () => {
    const { success } = await asyncAction(async () => {
      const selectedGroupId = form.get('permissions_group_id').value

      // Move members to the selected destination group before deleting
      if (memberIds.length > 0) {
        await addMembers({
          permissionGroupId: selectedGroupId,
          accessIds: memberIds,
        })
      }

      return permissionGroup.destroy()
    })

    if (success) onClose()
  }

  const onSubmit = () => {
    if (destinationSelected) {
      onDeleteConfirm()
    } else {
      setDestinationSelected(true)
    }
  }

  return (
    <Modal
      illustration="delete-cross"
      title={i18n.t('settings.permissions.delete.title', {
        name: permissionGroup.attributes.name,
      })}
      description={description}
      onClose={onClose}
      footer={
        <>
          {!destinationSelected && (
            <Button onClick={onSubmit} type="negative">
              {i18n.t('settings.permissions.delete.submit')}
            </Button>
          )}
          {destinationSelected && (
            <Button onClick={onDeleteConfirm} type="negative">
              {i18n.t('settings.permissions.delete.submit')}
            </Button>
          )}
        </>
      }
    >
      {!destinationSelected && (
        <Select
          field={form.get('permissions_group_id')}
          label={i18n.t('settings.permissions.delete.label')}
          options={selectOptions()}
        />
      )}
    </Modal>
  )
}

export default DeleteModal
